import { useEffect, useState } from 'react';
import axios from '../../axios';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import usePermisos from '../../hooks/usePermisos';
import NoPermissions from '../NoPermissions/NoPermissions';
import { Table, useTableSort } from '../UI';


const PERMISOS_REQUERIDOS = [
    'sucursals_read',
    'sucursals_create',
    'sucursals_update',
    'sucursals_delete'
];

const Sucursales = () => {
    const { permisos, loading: loadingPermisos, tienePermiso } = usePermisos(PERMISOS_REQUERIDOS);
    const [sucursales, setSucursales] = useState([]);
    const [sucursalesRaw, setSucursalesRaw] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loader, setLoader] = useState(true);

    useEffect(() => {
        axios.get('api/sucursals')
            .then(res => {
                setSucursales(res.data);
                setSucursalesRaw(res.data);
                setLoader(false);
            })
            .catch(e => {
                setLoader(false);
            });
    }, []);

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        
        if (value.length > 0) {
            const filtered = sucursalesRaw.filter((sucursal) =>
                sucursal.nombre_sucursal?.toLowerCase().includes(value) ||
                sucursal.domicilio?.toLowerCase().includes(value)
            );
            setSucursales(filtered);
        } else {
            setSucursales(sucursalesRaw);
        }
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setSucursales(sucursalesRaw);
    };

    const _delete = (sucursal) => {
        if (window.confirm("Realmente deseas borrar esta sucursal, si la borras no se podrá recuperar después")) {
            axios.delete(`api/sucursals/${sucursal.id}`)
                .then(res => {
                    alert(res.data.message);
                    setSucursales(sucursales.filter(u => u.id !== res.data.sucursal.id));
                    setSucursalesRaw(sucursalesRaw.filter(u => u.id !== res.data.sucursal.id));
                })
                .catch(e => {
                    if (e.response && e.response.status === 500 && e.response.data.message && e.response.data.message.includes("Integrity constraint violation")) {
                        alert('No puedes borrar una sucursal que está vinculada con otros datos');
                    }
                });
        }
    };

    // Handler para actualizar el valor local del input
    const handleToleranciaInput = (id, value) => {
        setSucursales(prev => prev.map(s => s.id === id ? { ...s, tolerancia_minutos: value } : s));
        setSucursalesRaw(prev => prev.map(s => s.id === id ? { ...s, tolerancia_minutos: value } : s));
    };

    // Handler para guardar en la base de datos al perder el foco
    const handleToleranciaBlur = (id, value) => {
        const minutos = parseInt(value, 10) || 0;
        axios.put(`api/sucursals/tolerancia/${id}`, { tolerancia_minutos: minutos })
            .then(() => {
                setSucursales(prev => prev.map(s => s.id === id ? { ...s, tolerancia_minutos: minutos } : s));
                setSucursalesRaw(prev => prev.map(s => s.id === id ? { ...s, tolerancia_minutos: minutos } : s));
            });
    };

    // Columnas para la tabla
    const columns = [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'nombre_sucursal', label: 'Sucursal', sortable: true },
        { key: 'domicilio', label: 'Domicilio' },
        {
            key: 'telefonos',
            label: 'Teléfonos',
            render: (_, row) => (
                <div className="space-y-0.5">
                    {[row.telefono1, row.telefono2, row.telefono3].filter(Boolean).map((tel, i) => (
                        <div key={i} className="text-sm">{tel}</div>
                    )) || '-'}
                </div>
            )
        },
        {
            key: 'horario',
            label: 'Horarios',
            render: (value) => value ? <span className="text-xs text-gray-700">{value}</span> : '-'
        },
        {
            key: 'horario_entregas',
            label: 'Entregas',
            render: (value) => value ? <span className="text-xs text-gray-700">{value}</span> : '-'
        },
        {
            key: 'tolerancia_minutos',
            label: 'Tolerancia (min)',
            align: 'center',
            render: (value, row) => (
                <input
                    type="number"
                    min={0}
                    className="dento-input w-10 text-center"
                    value={value ?? ''}
                    onChange={e => handleToleranciaInput(row.id, e.target.value)}
                    onBlur={e => handleToleranciaBlur(row.id, e.target.value)}
                />
            ),
        },
    ];

    // Acciones para la tabla
    const actions = [
        {
            type: 'edit',
            to: (row) => `${row.id}`,
            tooltip: 'Editar',
            permission: 'sucursals_update',
        },
        {
            type: 'delete',
            onClick: _delete,
            tooltip: 'Eliminar',
            permission: 'sucursals_delete',
        },
    ];

    if (loadingPermisos) {
        return (
            <div>
                <div className='container-admin-menu'>
                    <Breadcrumb hasButton={false} hasFilters={false} />
                </div>
                <div className='container-admin-container'>
                    <div className='container-admin z-depth-1 p-4'>
                        <div className="flex justify-center py-10">
                            <span className="text-gray-500">Cargando...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!tienePermiso('sucursals_read')) {
        return (
            <div>
                <div className='container-admin-menu'>
                    <Breadcrumb hasButton={tienePermiso('sucursals_create')} hasFilters={false} />
                </div>
                <div className='container-admin-container'>
                    <div className='container-admin z-depth-1'>
                        <NoPermissions />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className='container-admin-menu'>
                <Breadcrumb hasButton={tienePermiso('sucursals_create')} hasFilters={false} />
            </div>
            <div className='container-admin-container'>
                <div className='container-admin z-depth-1 p-4'>
                    {/* Barra de búsqueda */}
                    <div className="info-badge rounded-xl p-3 mb-4 flex flex-col md:flex-row items-center gap-3">
                        <div className="flex items-center gap-2">
                            <img
                                className="w-8 h-8"
                                src="/images/ubicacion.png"
                                alt="Sucursales"
                            />
                            <span className="text-white font-medium text-sm">Buscar:</span>
                        </div>
                        
                        <div className="flex-1 flex items-center gap-2">
                            <div className="relative flex-1">
                                <input
                                    type="text"
                                    className="w-full px-4 py-2 pr-10 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    placeholder="Buscar por nombre o domicilio..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                                <svg
                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            
                            {searchTerm && (
                                <button
                                    onClick={handleResetFilters}
                                    className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium flex items-center gap-1"
                                    title="Limpiar búsqueda"
                                >
                                    <img src="/images/clear-filters.png" alt="Limpiar" className="w-4 h-4" />
                                    Limpiar
                                </button>
                            )}
                        </div>
                    </div>

                    <Table
                        columns={columns}
                        data={sucursales}
                        actions={actions}
                        permissions={permisos}
                        loading={loader}
                        striped={true}
                        hoverable={true}
                        emptyMessage="No hay sucursales registradas"
                        sorting={{
                            field: null,
                            direction: null,
                            onSort: () => {}
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Sucursales;